// src/components/bugReport/BugReportModal.jsx
//
// Modal opened by BugReportFAB. Collects description + severity, auto-attaches
// URL/viewport/user-agent/recent-console-errors, optionally takes a screenshot
// (html2canvas lazy-loaded), posts to /api/report-bug.
//
// Reporter identity comes from useAuth(); the API resolves company_id from
// the JWT but the client also passes email/name for the notification email.

import { useEffect, useState } from 'react'
import { Modal } from '../ui/Modal'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import { supabase } from '../../lib/supabase'
import { queuedWrite } from '../../lib/queuedWrite'
import { Camera, Loader2, Send } from 'lucide-react'

const SEVERITIES = [
  { value: 'blocking',  label: 'Blocking — I can\'t do my job',                  color: 'red' },
  { value: 'annoying',  label: 'Annoying — slows me down or looks wrong',        color: 'amber' },
  { value: 'cosmetic',  label: 'Cosmetic — small visual or copy issue',          color: 'sage' },
]

export default function BugReportModal({ isOpen, onClose }) {
  const { user, profile } = useAuth()
  const toast = useToast()
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState('annoying')
  const [includeScreenshot, setIncludeScreenshot] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [screenshotPreview, setScreenshotPreview] = useState(null)
  const [capturing, setCapturing] = useState(false)

  // Capture screenshot on open (best effort) — gives the user a preview BEFORE
  // they start typing, so they know what's being sent. They can untoggle if
  // it's sensitive.
  useEffect(() => {
    if (!isOpen || !includeScreenshot || screenshotPreview) return
    let cancelled = false
    setCapturing(true)
    captureScreenshot()
      .then(dataUrl => { if (!cancelled) setScreenshotPreview(dataUrl) })
      .catch(() => { /* screenshot is best-effort */ })
      .finally(() => { if (!cancelled) setCapturing(false) })
    return () => { cancelled = true }
    // We intentionally only run this once on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  async function captureScreenshot() {
    const html2canvas = (await import('html2canvas')).default
    // Hide the modal during the snap so the screenshot shows what the user saw
    // BEFORE they clicked the bug button.
    const modal = document.querySelector('[data-bug-modal]')
    const prevDisplay = modal?.style.display
    if (modal) modal.style.display = 'none'
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale:   Math.min(window.devicePixelRatio || 1, 1.5),
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        x: window.scrollX,
        y: window.scrollY,
        width:  document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      })
      return canvas.toDataURL('image/jpeg', 0.75)
    } finally {
      if (modal) modal.style.display = prevDisplay || ''
    }
  }

  async function uploadScreenshot(dataUrl, reportId) {
    // dataUrl: "data:image/jpeg;base64,..."
    const blob = await (await fetch(dataUrl)).blob()
    const path = `${reportId}.jpg`
    const { error } = await supabase.storage
      .from('bug-screenshots')
      .upload(path, blob, { contentType: 'image/jpeg', upsert: true })
    if (error) throw error
    const { data } = supabase.storage.from('bug-screenshots').getPublicUrl(path)
    return data?.publicUrl || null
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!description.trim()) {
      toast.warning('Add a short description so we know what went wrong')
      return
    }
    setSubmitting(true)
    try {
      const recentErrors = (typeof window !== 'undefined' && window.__bugCtx?.getRecentErrors)
        ? window.__bugCtx.getRecentErrors()
        : []

      const payload = {
        reporter_user_id: user?.id || null,
        reporter_email:   user?.email || null,
        reporter_name:    profile?.full_name || profile?.name || user?.email || 'Unknown',
        url:              window.location.href,
        page_title:       document.title,
        viewport:         `${window.innerWidth}x${window.innerHeight}`,
        user_agent:       navigator.userAgent,
        description:      description.trim(),
        severity,
        console_errors:   recentErrors,
        screenshot_data_url: includeScreenshot ? screenshotPreview : null,
      }

      const res = await fetch('/api/report-bug', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      })

      if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(txt || `HTTP ${res.status}`)
      }

      const result = await res.json()

      // If the API didn't handle the screenshot upload itself (it doesn't —
      // we keep that client-side so the bucket policy doesn't need to allow
      // base64-via-server uploads), upload it now.
      if (includeScreenshot && screenshotPreview && result.id) {
        try {
          const url = await uploadScreenshot(screenshotPreview, result.id)
          if (url) {
            await queuedWrite('bug_reports', 'update', { screenshot_url: url }, result.id)
          }
        } catch (err) {
          console.warn('[BugReport] screenshot upload failed:', err.message)
        }
      }

      toast.success('Thanks — Aaron has been notified. You\'ll see this fixed soon.', 5000)
      onClose()
    } catch (err) {
      console.error('[BugReport] submit failed:', err)
      toast.error(`Couldn\'t send bug report: ${err.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div data-bug-modal>
      <Modal isOpen={isOpen} onClose={onClose} title="Report a bug" size="lg">
        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 overflow-y-auto">
          <p className="text-sm text-warm-600">
            Tell us what went wrong. We'll capture the page URL, recent errors,
            and (if you let us) a screenshot — so you don't have to.
          </p>

          <div>
            <label className="block text-xs font-semibold text-warm-700 mb-1.5">
              WHAT HAPPENED?
            </label>
            <textarea
              autoFocus
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Tried to add a new contact but the Save button doesn't do anything"
              rows={4}
              className="w-full px-3 py-2 border border-warm-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-400 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-warm-700 mb-1.5">
              HOW BAD IS IT?
            </label>
            <div className="space-y-1.5">
              {SEVERITIES.map(s => (
                <label
                  key={s.value}
                  className={`flex items-center gap-2.5 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                    severity === s.value
                      ? 'border-navy-500 bg-navy-50'
                      : 'border-warm-200 hover:bg-warm-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="severity"
                    value={s.value}
                    checked={severity === s.value}
                    onChange={(e) => setSeverity(e.target.value)}
                    className="text-navy-600"
                  />
                  <span className="text-sm text-warm-800">{s.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-warm-800 cursor-pointer">
              <input
                type="checkbox"
                checked={includeScreenshot}
                onChange={(e) => setIncludeScreenshot(e.target.checked)}
                className="rounded"
              />
              <Camera size={14} />
              Include a screenshot of the page
            </label>
            {includeScreenshot && (
              <div className="mt-2 border border-warm-200 rounded-lg overflow-hidden bg-warm-50 max-h-48">
                {capturing ? (
                  <div className="h-32 flex items-center justify-center text-warm-500 text-xs">
                    <Loader2 size={14} className="animate-spin mr-2" />
                    Capturing screenshot…
                  </div>
                ) : screenshotPreview ? (
                  <img src={screenshotPreview} alt="Screenshot preview" className="w-full" />
                ) : (
                  <div className="h-20 flex items-center justify-center text-warm-500 text-xs">
                    Screenshot will be captured on submit
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="text-xs text-warm-500 border-t border-warm-200 pt-3">
            Also sent automatically: page URL, browser type, viewport size,
            and any recent JavaScript errors in your console. No personal data
            beyond what's already visible above.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-warm-700 hover:bg-warm-100 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !description.trim()}
              className="px-4 py-2 text-sm bg-navy-600 hover:bg-navy-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              {submitting
                ? <><Loader2 size={14} className="animate-spin" /> Sending…</>
                : <><Send size={14} /> Send report</>
              }
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
